import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})

export class TranslationService {
  defaultLang = 'es';
  public static readonly availableLanguages: string[] = ['es', 'en'];
  public static readonly defaultLanguage: string = TranslationService.availableLanguages[0];
  public static readonly languageQueryParam = 'language';

  private translateService: TranslateService = inject(TranslateService);

  /**
   * Resolves UI language for WebView / browser sessions.
   * Priority: URL query → localStorage → OS/system language → browser language → `fallbackLanguage`.
   * Same contract as legal-policies WebView (`?language=en|es`).
   */
  public initLanguage(fallbackLanguage: string = TranslationService.defaultLanguage): void {
    const fromQuery = this.normalizeLanguage(this.getLanguageFromQuery());
    if (fromQuery) {
      this.setLanguage(fromQuery);
      return;
    }

    const fromStorage = this.normalizeLanguage(
      localStorage.getItem(TranslationService.languageQueryParam),
    );
    if (fromStorage) {
      this.setLanguage(fromStorage);
      return;
    }

    const fromSystem = this.normalizeLanguage(navigator.languages?.[0]);
    if (fromSystem) {
      this.setLanguage(fromSystem);
      return;
    }

    const fromBrowser = this.normalizeLanguage(
      this.translateService.getBrowserLang() ?? navigator.language,
    );
    if (fromBrowser) {
      this.setLanguage(fromBrowser);
      return;
    }

    this.setLanguage(this.normalizeLanguage(fallbackLanguage) ?? TranslationService.defaultLanguage);
  }

  /** @deprecated Prefer {@link initLanguage} — kept for call-site compatibility. */
  public initLanguageFromLocalStorage(): void {
    this.initLanguage();
  }

  public getCurrentLanguage(): string {
    const current = this.normalizeLanguage(this.translateService.currentLang);
    if (current) {
      return current;
    }
    return TranslationService.defaultLanguage;
  }

  public setLanguage(languageId: string): void {
    const normalized = this.normalizeLanguage(languageId);
    if (normalized) {
      this.translateService.use(normalized);
      localStorage.setItem(TranslationService.languageQueryParam, normalized);
    }
  }

  /** Maps `en-US` / `EN` / `es-PA` → `en` | `es`, or null if unsupported. */
  public normalizeLanguage(raw: string | null | undefined): string | null {
    if (!raw) {
      return null;
    }
    const base = raw.trim().toLowerCase().split(/[-_]/)[0];
    return TranslationService.availableLanguages.includes(base) ? base : null;
  }

  /** Reads `?language=` from the current URL (overridable in tests). */
  public getLanguageFromQuery(): string | null {
    return new URLSearchParams(window.location.search).get(
      TranslationService.languageQueryParam,
    );
  }
}
